'use client';

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { useSoftphone } from '@/hooks/use-softphone';
import type { ContactLookupStatus } from '@/hooks/use-contact-by-phone';
import type { CallSummary, SoftphoneIncomingCall, SoftphoneState } from '@/types/telephony';
import type { UserRole } from '@/types/common';
import type { ConnectionQuality } from '@/lib/connection-quality';
import type { Contact } from '@/types/contacts';

type SoftphoneRuntimeContextValue = {
  role: UserRole;
  setRole: (role: UserRole) => void;
  enabledForRole: boolean;
  // True when this tab owns the phone runtime; mirror tabs are false.
  hasRuntimeLease: boolean;
  // A pool line is established in this (owner) tab.
  poolLineLive: boolean;
  state: SoftphoneState;
  primaryDidNumber: string | null;
  activeCall: CallSummary | null;
  // Latest tracked-call snapshot (active + recently-ended, newest first) from
  // the SSE stream; identity only changes when content does.
  recentCalls: CallSummary[];
  refreshCallSnapshot: (options?: { force?: boolean }) => Promise<void>;
  localAudioMuted: boolean;
  setLocalAudioMuted: (muted: boolean) => void;
  // Persist + live-apply audio device preferences (see lib/audio-devices.ts).
  setAudioInputDevice: (deviceId: string | null) => Promise<void>;
  setAudioOutputDevice: (deviceId: string | null) => Promise<void>;
  reconnecting: boolean;
  // The owner's stuck-media rebuilds ran out: audio is not connecting.
  mediaFailed: boolean;
  // The owner's bridged call is sending the switch no audio.
  silentUplink: boolean;
  connectionQuality: ConnectionQuality;
  dialDestination: string;
  setDialDestination: (value: string) => void;
  matchedContact: Contact | null;
  matchedContactStatus: ContactLookupStatus;
  actionError: string | null;
  clearActionError: () => void;
  syncIdentityForCampaign: (campaignId: string | null | undefined) => Promise<void>;
  dial: () => Promise<void>;
  answer: () => Promise<void>;
  reject: () => Promise<void>;
  hangup: () => Promise<void>;
  incomingCall: SoftphoneIncomingCall | null;
  acceptIncomingCall: () => Promise<void>;
  declineIncomingCall: () => Promise<void>;
  sendDtmf: (tone: string) => void;
  recordTelemetryEvent: (
    eventName: string,
    severity?: 'info' | 'warning' | 'error',
    payload?: Record<string, unknown>,
    callId?: string | null
  ) => Promise<void>;
  // Ask the phone for a fresh REGISTER now (throttled; forwarded to the owner
  // tab from mirrors). The pool uses it while its seat waits for the line.
  reRegister: (reason: string) => void;
  // Restart the phone now, skipping any retry wait (forwarded to the owner tab).
  reconnect: () => void;
};

const SoftphoneRuntimeContext = createContext<SoftphoneRuntimeContextValue | null>(null);

export function SoftphoneRuntimeProvider({ children }: { children: ReactNode }) {
  const [role, setRole] = useState<UserRole>('admin');
  const softphone = useSoftphone(role);

  const value = useMemo<SoftphoneRuntimeContextValue>(
    () => ({
      role,
      setRole,
      enabledForRole: softphone.enabledForRole,
      hasRuntimeLease: softphone.hasRuntimeLease,
      poolLineLive: softphone.poolLineLive,
      state: softphone.state,
      primaryDidNumber: softphone.primaryDidNumber,
      activeCall: softphone.activeCall,
      recentCalls: softphone.recentCalls,
      refreshCallSnapshot: softphone.refreshCallSnapshot,
      localAudioMuted: softphone.localAudioMuted,
      setLocalAudioMuted: softphone.setLocalAudioMuted,
      setAudioInputDevice: softphone.setAudioInputDevice,
      setAudioOutputDevice: softphone.setAudioOutputDevice,
      reconnecting: softphone.reconnecting,
      mediaFailed: softphone.mediaFailed,
      silentUplink: softphone.silentUplink,
      connectionQuality: softphone.connectionQuality,
      dialDestination: softphone.dialDestination,
      setDialDestination: softphone.setDialDestination,
      matchedContact: softphone.matchedContact,
      matchedContactStatus: softphone.matchedContactStatus,
      actionError: softphone.actionError,
      clearActionError: softphone.clearActionError,
      syncIdentityForCampaign: softphone.syncIdentityForCampaign,
      dial: softphone.dial,
      answer: softphone.answer,
      reject: softphone.reject,
      hangup: softphone.hangup,
      incomingCall: softphone.incomingCall,
      acceptIncomingCall: softphone.acceptIncomingCall,
      declineIncomingCall: softphone.declineIncomingCall,
      sendDtmf: softphone.sendDtmf,
      recordTelemetryEvent: softphone.recordTelemetryEvent,
      reRegister: softphone.reRegister,
      reconnect: softphone.reconnect,
    }),
    [
      role,
      softphone.enabledForRole,
      softphone.hasRuntimeLease,
      softphone.poolLineLive,
      softphone.state,
      softphone.primaryDidNumber,
      softphone.activeCall,
      softphone.recentCalls,
      softphone.refreshCallSnapshot,
      softphone.localAudioMuted,
      softphone.setLocalAudioMuted,
      softphone.setAudioInputDevice,
      softphone.setAudioOutputDevice,
      softphone.reconnecting,
      softphone.mediaFailed,
      softphone.silentUplink,
      softphone.connectionQuality,
      softphone.dialDestination,
      softphone.setDialDestination,
      softphone.matchedContact,
      softphone.matchedContactStatus,
      softphone.actionError,
      softphone.clearActionError,
      softphone.syncIdentityForCampaign,
      softphone.dial,
      softphone.answer,
      softphone.reject,
      softphone.hangup,
      softphone.incomingCall,
      softphone.acceptIncomingCall,
      softphone.declineIncomingCall,
      softphone.sendDtmf,
      softphone.recordTelemetryEvent,
      softphone.reRegister,
      softphone.reconnect,
    ]
  );

  return (
    <SoftphoneRuntimeContext.Provider value={value}>
      <audio ref={softphone.remoteAudioRef} autoPlay playsInline style={{ display: 'none' }} />
      {children}
    </SoftphoneRuntimeContext.Provider>
  );
}

export function useSoftphoneRuntime() {
  return useContext(SoftphoneRuntimeContext);
}
